import React from 'react';
import { Link } from 'react-router-dom';
import '@fontsource/source-sans-pro';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LiveTrackingIcon from '@mui/icons-material/AirportShuttle';
import HistoryIcon from '@mui/icons-material/History';
import LastCheckIcon from '@mui/icons-material/WhereToVote';
import DelaysIcon from '@mui/icons-material/DepartureBoard';
import OperatorsIcon from '@mui/icons-material/Contacts';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import ZoneReportIcon from '@mui/icons-material/GpsFixed';
import FrequenciesIcon from '@mui/icons-material/PunchClock';
import BuildCircle from '@mui/icons-material/BuildCircle';
import LogOutIcon from '@mui/icons-material/Logout';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

import COLORS from '../styles/Colors';

const StyledListItemButton = styled(ListItemButton)(() => ({
  backgroundColor: 'inherit',
  color: COLORS.GRAY800,
  fontSize: 22,
  transition: '0.3s ease-out',
  '&:hover': {
    backgroundColor: 'rgb(181, 181, 185)',
    transform: 'scale(1.0625)',
    transition: '0.3s ease-in',
  },
  '&:focus': {
    backgroundColor: 'rgb(181, 181, 185)',
    transform: 'scale(1.0625)',
    transition: '0.3s ease-in',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 28,
  },
}));
const StyledItemSelected = styled(ListItem)(() => ({
  fontSize: 22,
  backgroundColor: 'inherit',
  color: COLORS.GREEN800,
  padding: '0.5rem 1rem',
  '& .MuiSvgIcon-root': {
    fontSize: 32,
  },
}));
const StyledLogOutButton = styled(ListItemButton)(() => ({
  backgroundColor: 'inherit',
  color: COLORS.RED700,
  fontSize: 26,
  transition: '0.3s ease-out',
  '&:hover': {
    backgroundColor: COLORS.ROSE100,
    transform: 'scale(1.0625)',
    transition: '0.3s ease-in',
  },
  '&:focus': {
    backgroundColor: COLORS.ROSE100,
    transform: 'scale(1.0625)',
    transition: '0.3s ease-in',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 32,
  },
}));
const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 40,
  color: 'inherit',
}));
const StyledListItemText = styled(ListItemText)(() => ({
  fontFamily: '"Source Sans Pro", "Roboto", Verdana, sans-serif',
  fontWeight: 'bold',
  marginTop: 6,
  marginBottom: 6,
}));

const MainMenuItem = ({
  pageItem,
  location,
  sessionout,
}) => {
  const defaultProps = {
    selecteditem: false,
    to: undefined,
    component: Link,
    href: undefined,
    target: undefined,
  };
  const itemsObject = {
    recorridos: {
      ...defaultProps,
      selecteditem: location === 'En curso',
      to: '/main',
      icon: LiveTrackingIcon,
      primary: 'En curso',
    },
    historial: {
      ...defaultProps,
      selecteditem: location === 'Historial',
      to: '/history',
      icon: HistoryIcon,
      primary: 'Historial',
    },
    reporte: {
      ...defaultProps,
      selecteditem: location === 'Reporte',
      to: '/lastcheckpoint',
      icon: LastCheckIcon,
      primary: 'Última Checada',
    },
    atrasos: {
      ...defaultProps,
      selecteditem: location === 'Atrasos',
      to: '/delays',
      icon: DelaysIcon,
      primary: 'Consultar Atrasos',
    },
    semanal: {
      ...defaultProps,
      selecteditem: location === 'Atrasos Semanal',
      to: '/semanalhistory',
      icon: DelaysIcon,
      primary: 'Atrasos Semanales',
    },
    operadores: {
      ...defaultProps,
      selecteditem: location === 'Operadores',
      to: '/operators',
      icon: OperatorsIcon,
      primary: 'Operadores',
    },
    reportezonas: {
      ...defaultProps,
      icon: ZoneReportIcon,
      primary: 'Reporte de Zonas',
      component: 'a',
      href: 'https://app.caebes.com/#/reportezonas',
      target: '_blank',
    },
    tiempos: {
      ...defaultProps,
      selecteditem: location === 'Tiempos',
      to: '/times',
      icon: TimelapseIcon,
      primary: 'Tiempos',
    },
    frecuencias: {
      ...defaultProps,
      selecteditem: location === 'Frecuencias',
      to: '/frequencies',
      icon: FrequenciesIcon,
      primary: 'Frecuencias',
    },
    checadores: {
      ...defaultProps,
      selecteditem: location === 'Checadores',
      to: '/checadores',
      icon: BuildCircle,
      primary: 'Checadores',
    },
    salidas: {
      ...defaultProps,
      selecteditem: location === 'Salidas',
      to: '/salidas',
      icon: RouteRoundedIcon,
      primary: 'Salidas',
    },
    logincheckers: {
      ...defaultProps,
      selecteditem: location === 'logincheckers',
      to: '/logincheckers',
      icon: AssignmentTurnedInIcon,
      primary: 'Entradas Checadores',
    },
  };
  const selecteditem = itemsObject?.[pageItem]?.selecteditem ?? false;
  const isLogout = (sessionout !== undefined);
  const to = itemsObject?.[pageItem]?.to ?? undefined;
  const ItemIcon = itemsObject?.[pageItem]?.icon ?? null;
  const primary = itemsObject?.[pageItem]?.primary ?? '';
  const component = itemsObject?.[pageItem]?.component ?? 'div';
  const href = itemsObject?.[pageItem]?.href ?? undefined;
  const target = itemsObject?.[pageItem]?.target ?? undefined;

  switch (true) {
    case selecteditem:
      return (
        <StyledItemSelected disablePadding>
          <StyledListItemIcon>
            {ItemIcon && <ItemIcon />}
          </StyledListItemIcon>
          <StyledListItemText disableTypography primary={primary} />
        </StyledItemSelected>
      );
    case isLogout:
      return (
        <ListItem disablePadding>
          <StyledLogOutButton component={component} onClick={sessionout}>
            <StyledListItemIcon>
              <LogOutIcon />
            </StyledListItemIcon>
            <StyledListItemText disableTypography primary="Cerrar sesión" />
          </StyledLogOutButton>
        </ListItem>
      );
    default:
      return (
        <ListItem disablePadding>
          <StyledListItemButton
            component={component}
            to={to}
            href={href}
            target={target}
          >
            <StyledListItemIcon>
              {ItemIcon && <ItemIcon />}
            </StyledListItemIcon>
            <StyledListItemText disableTypography primary={primary} />
          </StyledListItemButton>
        </ListItem>
      );
  }
};

export default MainMenuItem;
